import React, { useState, useRef, useEffect } from 'react';
import './MultiSelectDropdown.css';

/**
 * MultiSelectDropdown Component
 * A reusable dropdown that allows multiple value selection with checkboxes
 * Selected values are displayed as removable tags
 * 
 * Reusable across all modules - place in Shared/newfilterui
 */
const MultiSelectDropdown = ({
    options = [],
    selectedValues = [],
    onChange,
    onOpen,
    placeholder = "Select...",
    disabled = false,
    singleSelect = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Toggle option selection
    const toggleOption = (optionValue) => {
        if (singleSelect) {
            onChange([optionValue]);
            setIsOpen(false);
            return;
        }

        const newValues = selectedValues.includes(optionValue)
            ? selectedValues.filter(v => v !== optionValue)
            : [...selectedValues, optionValue];

        onChange(newValues);
    };

    // Remove a selected value (tag)
    const removeValue = (valueToRemove, e) => {
        e.stopPropagation();
        const newValues = selectedValues.filter(v => v !== valueToRemove);
        onChange(newValues);
    };

    // Clear all selections
    const clearAll = (e) => {
        e.stopPropagation();
        onChange([]);
        setSearchTerm('');
    };

    // Select all options
    const selectAll = (e) => {
        e.stopPropagation();
        const allValues = filteredOptions.map(opt => opt.value);
        onChange(allValues);
    };

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        String(option.label).toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get label for a value
    const getLabel = (value) => {
        const option = options.find(opt => opt.value === value);
        return option ? option.label : value;
    };

    return (
        <div
            className={`multiselect-dropdown ${disabled ? 'disabled' : ''}`}
            ref={dropdownRef}
        >
            <div
                className={`multiselect-trigger ${isOpen ? 'open' : ''}`}
                onClick={() => {
                    if (!disabled) {
                        const newIsOpen = !isOpen;
                        if (newIsOpen && onOpen) {
                            onOpen();
                        }
                        setIsOpen(newIsOpen);
                    }
                }}
            >
                <div className="multiselect-tags">
                    {selectedValues.length > 0 ? (
                        <>
                            {selectedValues.map((value, index) => (
                                <span key={index} className="multiselect-tag">
                                    {getLabel(value)}
                                    <button
                                        className="tag-remove-btn"
                                        onClick={(e) => removeValue(value, e)}
                                        type="button"
                                    >
                                        <i className="fa fa-times" />
                                    </button>
                                </span>
                            ))}
                        </>
                    ) : (
                        <span className="multiselect-placeholder">{placeholder}</span>
                    )}
                </div>

                <div className="multiselect-arrow">
                    <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} />
                </div>
            </div>

            {isOpen && (
                <div className="multiselect-dropdown-menu">
                    {/* Search Input */}
                    <div className="multiselect-search">
                        <i className="fa fa-search search-icon-small-fa serach-filter" />
                        <input
                            type="text"
                            className="multiselect-search-input"
                            placeholder="Search options..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="multiselect-actions">
                        {!singleSelect && (
                            <button
                                className="multiselect-action-btn"
                                onClick={selectAll}
                                type="button"
                            >
                                Select All
                            </button>
                        )}
                        <button
                            className="multiselect-action-btn"
                            onClick={clearAll}
                            type="button"
                        >
                            Clear All
                        </button>
                    </div>

                    {/* Options List */}
                    <div className="multiselect-options">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <label
                                    key={index}
                                    className={`multiselect-option ${selectedValues.includes(option.value) ? 'selected' : ''}`}
                                >
                                    <input
                                        type={singleSelect ? "radio" : "checkbox"}
                                        className="multiselect-checkbox"
                                        checked={selectedValues.includes(option.value)}
                                        onChange={() => toggleOption(option.value)}
                                    />
                                    <span className={singleSelect ? "dropdown-radio-custom" : "checkbox-custom"}></span>
                                    <span className="option-label">{option.label}</span>
                                </label>
                            ))
                        ) : (
                            <div className="no-options">
                                {searchTerm ? 'No matching options found' : 'No options available'}
                            </div>
                        )}
                    </div>

                    {/* Selected Count */}
                    {selectedValues.length > 0 && (
                        <div className="multiselect-footer">
                            <span className="selected-count">
                                {selectedValues.length} selected
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;
